import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, AlertTriangle, ExternalLink, Users, Link2, BookOpen } from 'lucide-react';
import useIsMobile from '../../utils/useIsMobile';
import { AREE, RISCHI, DIRETTIVO, COME_DECIDIAMO, NODI, CATENE } from './organizzazione.dati';

// ORGANIZZAZIONE (02/11/2026): pagina informale, solo lettura. Non spiega COME si fa una cosa
// (quello è la mappa dei percorsi) ma PERCHÉ conta: ogni tassello dipende da altri, se uno salta cadono i successivi.
// Contenuti tutti in organizzazione.dati.js.

const ORDINE_AREE = ['DIR', 'SEG', 'TEC', 'AMM', 'EVE', 'COM'];
const IDS = Object.keys(NODI);

// Chi dipende da chi, nei due sensi
const PRIMA = {};
IDS.forEach(id => { PRIMA[id] = []; });
IDS.forEach(id => NODI[id].porta.forEach(d => { if (PRIMA[d]) PRIMA[d].push(id); }));

// Livelli dell'effetto domino a valle (o a monte) di un tassello
const livelli = (id, verso) => {
  const visti = new Set([id]);
  const out = [];
  let fronte = [id];
  while (fronte.length) {
    const nuovo = [];
    fronte.forEach(x => (verso === 'giu' ? NODI[x].porta : PRIMA[x]).forEach(y => { if (!visti.has(y)) { visti.add(y); nuovo.push(y); } }));
    if (nuovo.length) out.push(nuovo);
    fronte = nuovo;
  }
  return out;
};

// Colonna = passo più lungo da un tassello di partenza: il disegno scorre da sinistra (cause) a destra (conseguenze)
const PROF = {};
const prof = (id, pila = new Set()) => {
  if (PROF[id] != null) return PROF[id];
  if (pila.has(id)) return 0;
  pila.add(id);
  const p = PRIMA[id].length ? Math.max(...PRIMA[id].map(x => prof(x, pila) + 1)) : 0;
  pila.delete(id);
  PROF[id] = p;
  return p;
};
IDS.forEach(id => prof(id));

const NW = 134, NH = 54, GX = 150, GY = 64, PAD = 20;
const COLONNE = [];
IDS.forEach(id => { (COLONNE[PROF[id]] = COLONNE[PROF[id]] || []).push(id); });
COLONNE.forEach(c => c.sort((a, b) => ORDINE_AREE.indexOf(NODI[a].area) - ORDINE_AREE.indexOf(NODI[b].area)));
const RIGHE_MAX = Math.max(...COLONNE.map(c => c.length));
const POS = {};
COLONNE.forEach((c, i) => {
  const off = (RIGHE_MAX - c.length) * GY / 2;
  c.forEach((id, j) => { POS[id] = { x: PAD + i * GX, y: PAD + 26 + off + j * GY }; });
});
const W = PAD * 2 + (COLONNE.length - 1) * GX + NW;
const H = PAD * 2 + 26 + RIGHE_MAX * GY;

const aCapo = (testo, max = 17) => {
  const parole = testo.split(' '); const righe = []; let r = '';
  parole.forEach(p => { if ((r + ' ' + p).trim().length > max && r) { righe.push(r); r = p; } else r = (r + ' ' + p).trim(); });
  if (r) righe.push(r);
  return righe.slice(0, 3);
};

const areaDi = (id) => AREE[NODI[id].area];

function Chip({ area, piccolo }) {
  const a = AREE[area];
  return <span style={{ ...s.chip, background: a.colore + '18', color: a.colore, borderColor: a.colore + '55', ...(piccolo ? { fontSize: '10px', padding: '2px 8px' } : {}) }}>{a.nome.toUpperCase()}</span>;
}

function Rischio({ r }) {
  return <span style={{ ...s.chip, background: RISCHI[r] + '14', color: RISCHI[r], borderColor: RISCHI[r] + '55' }}><AlertTriangle size={11} /> RISCHIO {r}</span>;
}

function Tassello({ id, onClick, attivo }) {
  const n = NODI[id]; const a = areaDi(id);
  return (
    <div role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onClick(id)} onClick={() => onClick(id)} style={{ ...s.tassello, borderLeft: `4px solid ${a.colore}`, ...(attivo ? { background: a.colore + '14' } : {}) }}>
      {n.t}
    </div>
  );
}

// --- Scheda 1: la rete ---
function Rete({ sel, setSel, mobile }) {
  const giu = useMemo(() => livelli(sel, 'giu'), [sel]);
  const su = useMemo(() => livelli(sel, 'su'), [sel]);
  const aValle = new Set(giu.flat());
  const aMonte = new Set(su.flat());
  const n = NODI[sel]; const a = areaDi(sel);
  const stato = (id) => id === sel ? 'sel' : aValle.has(id) ? 'giu' : aMonte.has(id) ? 'su' : 'off';

  return (
    <div>
      <p style={s.spiega}>
        Tocca un tassello: in <b style={{ color: '#B42318' }}>rosso</b> tutto quello che cade se quel tassello manca,
        in <b style={{ color: '#1F5FAF' }}>blu</b> quello che serve prima. I colori dei bordi dicono di quale area è il tassello.
      </p>

      {mobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          {ORDINE_AREE.map(ar => (
            <div key={ar}>
              <div style={{ marginBottom: '6px' }}><Chip area={ar} piccolo /></div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {IDS.filter(id => NODI[id].area === ar).map(id => <Tassello key={id} id={id} onClick={setSel} attivo={id === sel} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...s.card, padding: '8px', overflowX: 'auto', marginBottom: '16px' }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: '760px', display: 'block' }} role="img" aria-label="Rete dei tasselli collegati">
            <text x={PAD} y={PAD + 8} fontSize="11" fontWeight="700" fill="var(--text-dim)" letterSpacing="1">DA DOVE PARTE</text>
            <text x={W - PAD} y={PAD + 8} fontSize="11" fontWeight="700" fill="var(--text-dim)" letterSpacing="1" textAnchor="end">DOVE ARRIVA →</text>
            {IDS.flatMap(id => NODI[id].porta.map(d => {
              const p = POS[id], q = POS[d];
              const x1 = p.x + NW, y1 = p.y + NH / 2, x2 = q.x, y2 = q.y + NH / 2;
              const mx = (x1 + x2) / 2;
              const accesa = (id === sel || aValle.has(id)) && aValle.has(d) ? 'giu'
                : (d === sel || aMonte.has(d)) && aMonte.has(id) ? 'su' : null;
              return <path key={id + d} d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none"
                stroke={accesa === 'giu' ? '#B42318' : accesa === 'su' ? '#1F5FAF' : 'var(--border2)'}
                strokeWidth={accesa ? 2.2 : 1} opacity={accesa ? 0.9 : 0.6} />;
            }))}
            {IDS.map(id => {
              const p = POS[id]; const st = stato(id); const col = areaDi(id).colore;
              const righe = aCapo(NODI[id].t);
              const bordo = st === 'sel' ? col : st === 'giu' ? '#B42318' : st === 'su' ? '#1F5FAF' : 'var(--border2)';
              return (
                <g key={id} onClick={() => setSel(id)} style={{ cursor: 'pointer' }} opacity={st === 'off' ? 0.45 : 1}>
                  <title>{NODI[id].t}</title>
                  <rect x={p.x} y={p.y} width={NW} height={NH} rx="10" fill={st === 'sel' ? col : 'var(--bg-card)'} stroke={bordo} strokeWidth={st === 'off' ? 1 : 2} />
                  <rect x={p.x} y={p.y + 8} width="4" height={NH - 16} rx="2" fill={st === 'sel' ? '#fff' : col} />
                  <text x={p.x + 12} y={p.y + NH / 2 - (righe.length - 1) * 7.5 + 4} fontSize="13" fontWeight="600" fill={st === 'sel' ? '#fff' : 'var(--text)'}>
                    {righe.map((r, i) => <tspan key={i} x={p.x + 12} dy={i ? 15 : 0}>{r}</tspan>)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'minmax(0,1.1fr) minmax(0,1fr)', gap: '14px' }}>
        <div style={{ ...s.card, borderTop: `4px solid ${a.colore}` }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}><Chip area={n.area} /><Rischio r={n.rischio} /></div>
          <div style={s.nomeNodo}>{n.t}</div>
          <p style={s.testo}>{n.cos}</p>
          <div style={s.seManca}><b>SE MANCA:</b> {n.manca}</div>
          {n.dove && <Link to={n.dove} style={s.link}><ExternalLink size={13} /> Apri nel gestionale</Link>}
          {PRIMA[sel].length > 0 && (
            <div style={{ marginTop: '14px' }}>
              <div style={s.label}>PER AVERLO SERVE PRIMA</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {PRIMA[sel].map(id => <Tassello key={id} id={id} onClick={setSel} />)}
              </div>
            </div>
          )}
        </div>

        <div style={s.card}>
          <div style={s.cardTitle}>EFFETTO DOMINO</div>
          {giu.length === 0
            ? <p style={s.testo}>Qui finisce la catena: è una conseguenza, non trascina altro.</p>
            : giu.map((liv, i) => (
              <div key={i} style={{ marginTop: '10px' }}>
                <div style={s.label}>{i === 0 ? 'CADE SUBITO' : `${i + 1}° PASSO`}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                  {liv.map(id => (
                    <div role="button" tabIndex={0} key={id} onClick={() => setSel(id)} style={{ ...s.riga, borderLeft: `4px solid ${areaDi(id).colore}` }}>
                      <span style={{ fontWeight: 700 }}>{NODI[id].t}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{NODI[id].manca}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// --- Scheda 2: le catene raccontate ---
function Catene({ vai }) {
  return (
    <div style={s.grid}>
      {CATENE.map(c => (
        <div key={c.titolo} style={s.card}>
          <div style={s.cardTitle}>{c.titolo.toUpperCase()}</div>
          <p style={{ ...s.testo, marginTop: '4px' }}>{c.intro}</p>
          <div style={{ marginTop: '12px' }}>
            {c.passi.map((id, i) => {
              const prec = i > 0 ? NODI[c.passi[i - 1]].area : null;
              const cambia = prec && prec !== NODI[id].area;
              return (
                <div key={id}>
                  {i > 0 && (
                    <div style={s.freccia}>
                      <ArrowDown size={14} />
                      {cambia && <span>passa da <b>{AREE[prec].nome}</b> a <b>{areaDi(id).nome}</b></span>}
                    </div>
                  )}
                  <div role="button" tabIndex={0} onClick={() => vai(id)} style={{ ...s.riga, borderLeft: `4px solid ${areaDi(id).colore}` }}>
                    <span style={{ fontWeight: 700 }}>{NODI[id].t}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{i === 0 ? `Se manca: ${NODI[id].manca}` : NODI[id].manca}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Scheda 3: chi fa cosa ---
function ChiFaCosa({ vai }) {
  const scambi = useMemo(() => {
    const r = {};
    ORDINE_AREE.forEach(a => { r[a] = { da: new Set(), a: new Set() }; });
    IDS.forEach(id => NODI[id].porta.forEach(d => {
      const x = NODI[id].area, y = NODI[d].area;
      if (x !== y) { r[x].a.add(y); r[y].da.add(x); }
    }));
    return r;
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={s.card}>
        <div style={s.cardTitle}><Users size={16} style={{ verticalAlign: '-2px' }} /> IL DIRETTIVO</div>
        <div style={{ ...s.grid, marginTop: '10px' }}>
          {DIRETTIVO.map(d => (
            <div key={d.ruolo} style={s.persona}>
              <div style={s.label}>{d.ruolo.toUpperCase()}</div>
              <div style={{ fontWeight: 700, margin: '2px 0 6px', color: d.nome ? 'var(--text)' : 'var(--text-dim)' }}>{d.nome || 'da indicare'}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', lineHeight: 1.5 }}>{d.fa}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}><BookOpen size={16} style={{ verticalAlign: '-2px' }} /> COME DECIDIAMO</div>
        <p style={{ ...s.testo, marginTop: '4px' }}>Non è una piramide in cui uno ordina e gli altri eseguono: lo statuto mette al centro il direttivo, che decide insieme.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
          {COME_DECIDIAMO.map(([t, d]) => (
            <div key={t} style={s.persona}><b>{t}</b><div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginTop: '2px' }}>{d}</div></div>
          ))}
        </div>
      </div>

      <div>
        <div style={{ ...s.cardTitle, margin: '4px 0 10px' }}><Link2 size={16} style={{ verticalAlign: '-2px' }} /> LE AREE E COSA SI PASSANO</div>
        <div style={s.grid}>
          {ORDINE_AREE.map(ar => {
            const a = AREE[ar];
            return (
              <div key={ar} style={{ ...s.card, borderTop: `4px solid ${a.colore}` }}>
                <div style={{ ...s.nomeNodo, color: a.colore }}>{a.nome}</div>
                <p style={s.testo}>{a.cosa}</p>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-dim)', margin: '6px 0' }}>Responsabile: <b>da decidere in direttivo</b></div>
                <div style={s.label}>I SUOI TASSELLI</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '6px 0 10px' }}>
                  {IDS.filter(id => NODI[id].area === ar).map(id => <Tassello key={id} id={id} onClick={vai} />)}
                </div>
                {scambi[ar].da.size > 0 && <div style={s.scambio}><ArrowRight size={12} /> riceve da: {[...scambi[ar].da].map(x => AREE[x].nome).join(', ')}</div>}
                {scambi[ar].a.size > 0 && <div style={s.scambio}><ArrowRight size={12} /> passa a: {[...scambi[ar].a].map(x => AREE[x].nome).join(', ')}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const SCHEDE = [['rete', 'TUTTO È COLLEGATO'], ['catene', 'LE CATENE'], ['chi', 'CHI FA COSA']];

export default function Organizzazione() {
  const mobile = useIsMobile();
  const [scheda, setScheda] = useState('rete');
  const [sel, setSel] = useState('cf');
  const vai = (id) => { setSel(id); setScheda('rete'); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>ORGANIZZAZIONE</h1>
          <p style={s.sub}>
            Pagina nostra, informale. Nessun lavoro in B-Cheers sta da solo: ogni informazione che arriva (o non arriva) ne
            sblocca o ne blocca altre, anche in aree lontane. Qui si vede chi porta cosa e cosa succede se un pezzo manca.
            Per sapere <i>come</i> si fa una cosa c'è la <a href="/mappa/percorsi.html" target="_blank" rel="noreferrer" style={{ color: 'var(--accent-strong)' }}>mappa dei percorsi</a>.
          </p>
        </div>
      </div>

      <div style={s.pills}>
        {SCHEDE.map(([k, l]) => (
          <button key={k} onClick={() => setScheda(k)} style={{ ...s.pill, ...(scheda === k ? s.pillOn : {}) }}>{l}</button>
        ))}
      </div>

      {scheda === 'rete' && <Rete sel={sel} setSel={setSel} mobile={mobile} />}
      {scheda === 'catene' && <Catene vai={vai} />}
      {scheda === 'chi' && <ChiFaCosa vai={vai} />}

      <p style={{ ...s.spiega, marginTop: '20px' }}>
        I riferimenti agli articoli sono allo statuto caricato nel gestionale. Non è un parere legale o fiscale:
        per i casi concreti vale quello che dicono il commercialista e l'ente di affiliazione.
      </p>
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '780px', lineHeight: 1.5 },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' },
  pill: { padding: '8px 14px', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '999px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer' },
  pillOn: { background: '#F4600C', borderColor: '#F4600C', color: '#fff' },
  spiega: { color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', margin: '0 0 12px', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', minWidth: 0 },
  cardTitle: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight: '700', letterSpacing: '2px' },
  nomeNodo: { fontFamily: "'Barlow Condensed'", fontSize: '24px', fontWeight: '800', letterSpacing: '0.5px', marginBottom: '6px' },
  testo: { color: 'var(--text)', fontSize: 'var(--font-size-sm)', lineHeight: 1.55, margin: 0 },
  seManca: { background: '#B4231810', border: '1px solid #B4231840', color: '#7A1A12', borderRadius: '8px', padding: '10px 12px', marginTop: '10px', fontSize: 'var(--font-size-sm)', lineHeight: 1.5 },
  link: { display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px', color: 'var(--accent-strong)', fontWeight: 700, fontSize: 'var(--font-size-xs)', textDecoration: 'none' },
  label: { fontSize: '11px', letterSpacing: '1.5px', fontWeight: '700', color: 'var(--text-dim)' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '4px', border: '1px solid', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px' },
  tassello: { display: 'inline-block', boxSizing: 'border-box', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '6px 10px', color: 'var(--text)', fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer', textAlign: 'left' },
  riga: { boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '3px', width: '100%', textAlign: 'left', background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', fontSize: 'var(--font-size-sm)', lineHeight: 1.45, color: 'var(--text)', cursor: 'pointer' },
  freccia: { display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', padding: '5px 0 5px 10px' },
  persona: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '10px', padding: '12px' },
  scambio: { display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginTop: '4px' },
};
